// utils/imageCompress.js
// Client-side image compression — resizes photos to max 1920px and compresses
// to JPEG ~80% quality before uploading.  Reduces typical phone photos from
// 4-8MB down to 200-500KB, making mobile uploads fast over any connection.

const MAX_DIMENSION = 1920
const QUALITY       = 0.80

/**
 * Compress an image File to a smaller JPEG Blob.
 * Returns the original file unchanged if it's not an image or already small.
 * @param {File} file
 * @param {object} opts  { maxDimension, quality }
 * @returns {Promise<File>}
 */
export async function compressImage(file, opts = {}) {
  // Skip non-images or very small files (<100KB)
  if (!file.type.startsWith('image/') || file.size < 100_000) return file

  // Skip SVGs (vector, can't compress with canvas)
  if (file.type === 'image/svg+xml') return file

  const maxDim = opts.maxDimension || MAX_DIMENSION
  const qual   = opts.quality      || QUALITY

  return new Promise((resolve) => {
    const img = new Image()
    const url = URL.createObjectURL(file)

    img.onload = () => {
      URL.revokeObjectURL(url)

      let { width, height } = img

      // Only resize if larger than max dimension
      if (width > maxDim || height > maxDim) {
        if (width > height) {
          height = Math.round(height * (maxDim / width))
          width  = maxDim
        } else {
          width  = Math.round(width * (maxDim / height))
          height = maxDim
        }
      }

      const canvas = document.createElement('canvas')
      canvas.width  = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, width, height)

      canvas.toBlob(
        (blob) => {
          if (!blob || blob.size >= file.size) {
            // Compression made it bigger (rare) — use original
            resolve(file)
          } else {
            // Preserve original filename but change extension to .jpg
            const name = file.name.replace(/\.[^.]+$/, '.jpg')
            resolve(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }))
          }
        },
        'image/jpeg',
        qual
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve(file) // fallback to original on error
    }

    img.src = url
  })
}
